import { makeLoginGuard } from "../../router/guards";
import { useConfigController, useConfigStore } from "@/config/useConfigStore";
import { CONFIG } from "@/common/config";
import { Route, NavigationGuardNext } from "vue-router";
import { useWordPressLogin } from "@/auth/auth-constants";

export const LAUNCH_ROUTES_PATHS_V2_DIR = "v2";
export const LAUNCH_ROUTES_PATHS_V2_BASE = "#/" + LAUNCH_ROUTES_PATHS_V2_DIR;

export const LAUNCH_ROUTES_PATHS_V2 = {
  HOME_V2: "home-v2",
  LOGIN_V2: "login-v2",
  REGISTER_V2: "register-v2",
  FORGOT_V2: "forgot-v2",
  RESET_V2: "reset_pwd", //  N.B. the underscore, not a dash.
  ENTRY_V2: "entry-v2",
  ENTRY_V3: "entry-v3",
  SHOW_ENTRY_v2: "show-entry-v2",
  ATHLETE_V2: "athlete-v2",
  ATHLETE_PB_V2: "athlete-pb-v2",
  ATHLETES_V2: "athletes-v2",
  SHOP_V2: "shop-v2",
  SCHED_COMP_PUBLIC: "schedule-v2",
  MESSAGES_V2: "messages-v2",
  USER_SEARCH_V2: "user-search-v2",
  USER_V2: "user-v2",
  CLUB_SEARCH_V2: "club-search-v2",
  CLUB_V2: "club-v2",
  ORG_SEARCH_V2: "org-search-v2",

  BUILDER_CHOICE_V2: "builder-choice-v2",
  BUILDER_V2: "builder-v2",
  STEPPER_BUILDER_V2: "stepper-builder-v2",
  CONTACT_ORGANISER_V2: "contact-organiser-v2",
  RESULTS_IMPORT_FILE: "results-import-file",
  RESULTS_IMPORT_V2: "results-import",
  RESULTS_MANUAL: "results-v2",
  RESULTS_ENTRY: "results-entry-v2",
  CHECK_IN_V2: "checkin-v2",
  TICKET_ADMIN_V2: "ticket-admin-v2",
  TICKET_FORM_V2: "ticket-v2",
  TICKET_FORM_GATEKEEPER: "ticket-form-gatekeeper",

  SCOREBOARD_OUTPUT_LIST: "scoreboard-output-list",
  SCOREBOARD_SCHEDULE_LIST: "scoreboard-schedule-list",
  BIBS_V2: "bibs-v2",

  FORM_GENERIC_EXAMPLE_V2: "form-generic-example-v2",
  AGE_GROUP_LIST_V2: "age-group-list-v2",
  AGE_GROUP_EDIT_V2: "age-group-edit-v2",
  XC_UPLOAD_V3: "xc-upload",
  COMP_EVENT_ACTIONS_V2: "actions-v2",

  STRIPE_USERS_V2: "stripe-users-v2",
  HELP_FORM_LIST_V2: "help-form-list-v2",
  SECONDARY_GRID_V2: "sec-grid-v2",
  REPORTS_V2: "reports-v2",
  WAITLIST_V2: "manage-entries",
};
// Bind a guard instance here to avoid circular imports with index.ts
const isLoggedInGuardV2 = makeLoginGuard(LAUNCH_ROUTES_PATHS_V2.LOGIN_V2);

type LaunchRoutesV2 = typeof LAUNCH_ROUTES_PATHS_V2;
export type LaunchRouteKeyV2 = keyof LaunchRoutesV2;
export type LaunchRouteValueV2 = LaunchRoutesV2[keyof LaunchRoutesV2];

export const launchRoutesV2: any = [
  {
    path: "",
    redirect: LAUNCH_ROUTES_PATHS_V2.HOME_V2,
  },
  {
    name: LAUNCH_ROUTES_PATHS_V2.HOME_V2,
    path: LAUNCH_ROUTES_PATHS_V2.HOME_V2,
    component: () => {
      return import("../../public/entry-public/v2/entry-public-v2.vue");
    },
    meta: {
      title: "Home",
    },
  },
  {
    name: LAUNCH_ROUTES_PATHS_V2.LOGIN_V2,
    path: LAUNCH_ROUTES_PATHS_V2.LOGIN_V2,
    component: () => {
      return import("../../auth/v2/login-container-v2.vue");
    },
    meta: {
      title: "Login",
    },
    beforeEnter: (to: Route, from: Route, next: NavigationGuardNext) => {
      // Check if external auth is enabled and configured
      const configStore = useConfigStore();
      const configController = useConfigController();

      if (useWordPressLogin()) {
        // Construct WordPress login URL
        const wordpressLoginPath = "/wp-login.php";
        const baseUrl = CONFIG.E4S_HOST || window.location.origin;
        const redirectUrl = baseUrl + wordpressLoginPath;

        // Add return URL parameter to redirect back after WordPress login
        //const returnUrl = encodeURIComponent(window.location.href);
        //const finalRedirectUrl = `${redirectUrl}?redirect_to=${returnUrl}`;

        // Redirect to WordPress login URL with return URL
        window.location.href = redirectUrl;
        return;
      }

      // Continue to normal login component
      next();
    },
  },
  {
    name: LAUNCH_ROUTES_PATHS_V2.REGISTER_V2,
    path: LAUNCH_ROUTES_PATHS_V2.REGISTER_V2,
    component: () => {
      return import("../../auth/v2/register/register-container-v2.vue");
    },
    meta: {
      title: "Register",
    },
    beforeEnter: (to: Route, from: Route, next: NavigationGuardNext) => {
      // Check if WordPress registration should be used
      if (useWordPressLogin()) {
        // Construct WordPress registration URL
        const baseUrl = CONFIG.E4S_HOST || window.location.origin;
        const wordpressRegisterUrl = `${baseUrl}/wp-login.php?action=register`;

        // Add return URL parameter to redirect back after WordPress registration
        const returnUrl = encodeURIComponent(window.location.href);
        const finalRedirectUrl = `${wordpressRegisterUrl}&redirect_to=${returnUrl}`;

        // Redirect to WordPress registration URL
        window.location.href = finalRedirectUrl;
        return;
      }

      // Continue to normal registration component
      next();
    },
  },
  {
    name: LAUNCH_ROUTES_PATHS_V2.RESET_V2,
    path: LAUNCH_ROUTES_PATHS_V2.RESET_V2,
    component: () => {
      return import("../../auth/v2/reset/reset-container-v2.vue");
    },
    meta: {
      title: "Reset",
    },
    beforeEnter: (to: Route, from: Route, next: NavigationGuardNext) => {
      if (!useWordPressLogin()) {
        next();
        return;
      }
      ///wp-login.php?action=lostpassword
      const wordpressLoginPath = "/wp-login.php?action=lostpassword";
      const baseUrl = CONFIG.E4S_HOST || window.location.origin;
      const redirectUrl = baseUrl + wordpressLoginPath;
      window.location.href = redirectUrl;
    },
  },
  {
    name: LAUNCH_ROUTES_PATHS_V2.FORGOT_V2,
    path: LAUNCH_ROUTES_PATHS_V2.FORGOT_V2,
    component: () => {
      return import("../../auth/v2/forgot/forgot-container-v2.vue");
    },
    meta: {
      title: "Forgot",
    },
    beforeEnter: (to: Route, from: Route, next: NavigationGuardNext) => {
      if (!useWordPressLogin()) {
        next();
        return;
      }
      ///wp-login.php?action=lostpassword
      const wordpressLoginPath = "/wp-login.php?action=lostpassword";
      const baseUrl = CONFIG.E4S_HOST || window.location.origin;
      const redirectUrl = baseUrl + wordpressLoginPath;
      window.location.href = redirectUrl;
    },
  },
  {
    name: LAUNCH_ROUTES_PATHS_V2.ENTRY_V2,
    path: LAUNCH_ROUTES_PATHS_V2.ENTRY_V2 + "/:id",
    component: () => {
      return import("../../entry/v2/entry-route-v2.vue");
    },
    meta: {
      title: (params: { id: string }) => {
        return params.id + ": Entry";
      },
    },
  },
  {
    name: LAUNCH_ROUTES_PATHS_V2.ENTRY_V3,
    path: LAUNCH_ROUTES_PATHS_V2.ENTRY_V3 + "/:id",
    component: () => {
      return import("../../entry/v3/entry-route-v3.vue");
    },
    meta: {
      title: (params: { id: string }) => {
        return params.id + ": Entry V3";
      },
    },
  },
  {
    name: LAUNCH_ROUTES_PATHS_V2.SHOW_ENTRY_v2,
    path: LAUNCH_ROUTES_PATHS_V2.SHOW_ENTRY_v2 + "/:id",
    component: () => {
      return import("../../public/entry-public/v2/entry-direct-v2.vue");
    },
    meta: {
      title: (params: { id: string }) => {
        return params.id + ": Comp";
      },
    },
  },
  {
    name: LAUNCH_ROUTES_PATHS_V2.ATHLETE_V2,
    path: LAUNCH_ROUTES_PATHS_V2.ATHLETE_V2 + "/:id",
    beforeEnter: isLoggedInGuardV2,
    component: () => {
      return import("../../athlete/v2/form/athlete-form-route-v2.vue");
    },
    meta: {
      title: (params: { id: string }) => {
        return params.id + ": Athlete";
      },
    },
  },
  {
    name: LAUNCH_ROUTES_PATHS_V2.ATHLETES_V2,
    path: LAUNCH_ROUTES_PATHS_V2.ATHLETES_V2,
    beforeEnter: isLoggedInGuardV2,
    component: () => {
      return import("../../athlete/v2/search/athlete-search-route-v2.vue");
    },
    meta: {
      title: "Athletes",
    },
  },
  {
    name: LAUNCH_ROUTES_PATHS_V2.SCHED_COMP_PUBLIC,
    path: LAUNCH_ROUTES_PATHS_V2.SCHED_COMP_PUBLIC + "/:id",
    component: () => {
      return import(
        "../../competition/results/public/schedule/v2/schedule-comp-public-route-v2.vue"
      );
    },
    meta: {
      title: "Schedule",
    },
  },
  {
    name: LAUNCH_ROUTES_PATHS_V2.MESSAGES_V2,
    path: LAUNCH_ROUTES_PATHS_V2.MESSAGES_V2 + "/:compId?",
    beforeEnter: isLoggedInGuardV2,
    component: () => {
      return import(
        "../../email-message/grid/v2/email-message-grid-route-v2.vue"
      );
    },
    meta: {
      title: (params: { compId: number }) => {
        return (
          "Messages" +
          (params.compId && params.compId > 0
            ? " for Comp: " + params.compId
            : "")
        );
      },
    },
  },
  {
    name: LAUNCH_ROUTES_PATHS_V2.USER_SEARCH_V2,
    path: LAUNCH_ROUTES_PATHS_V2.USER_SEARCH_V2,
    beforeEnter: isLoggedInGuardV2,
    component: () => {
      return import("../../admin/user/v2/user-search-v2.vue");
    },
    meta: {
      title: () => {
        return "Users";
      },
    },
  },
  {
    name: LAUNCH_ROUTES_PATHS_V2.USER_V2,
    path: LAUNCH_ROUTES_PATHS_V2.USER_V2 + "/:id?",
    beforeEnter: isLoggedInGuardV2,
    component: () => {
      return import("../../admin/user/v2/user-route-container-v2.vue");
    },
    meta: {
      title: () => {
        return "User";
      },
    },
  },
  {
    name: LAUNCH_ROUTES_PATHS_V2.BUILDER_CHOICE_V2,
    path: LAUNCH_ROUTES_PATHS_V2.BUILDER_CHOICE_V2,
    beforeEnter: isLoggedInGuardV2,
    component: () => {
      return import("../../builder/form/builder-choice.vue");
    },
    meta: {
      title: "Create Competition",
    },
  },
  {
    name: LAUNCH_ROUTES_PATHS_V2.BUILDER_V2,
    path: LAUNCH_ROUTES_PATHS_V2.BUILDER_V2 + "/:id",
    beforeEnter: isLoggedInGuardV2,
    component: () => {
      return import("../../builder/form/builder-form.vue");
    },
    meta: {
      title: (params: { id: number }) => {
        return params.id + ": Builder";
      },
    },
  },
  {
    name: LAUNCH_ROUTES_PATHS_V2.STEPPER_BUILDER_V2,
    path: LAUNCH_ROUTES_PATHS_V2.STEPPER_BUILDER_V2 + "/:id?",
    beforeEnter: isLoggedInGuardV2,
    component: () => {
      return import("../../builder/form/stepper/stepper-builder-route.vue");
    },
    meta: {
      title: "Create Competition",
    },
  },
  {
    name: LAUNCH_ROUTES_PATHS_V2.FORM_GENERIC_EXAMPLE_V2,
    path: LAUNCH_ROUTES_PATHS_V2.FORM_GENERIC_EXAMPLE_V2,
    component: () => {
      return import("../../common/ui/layoutV2/form/form-generic-example.vue");
    },
    meta: {
      title: () => {
        return "Example Form";
      },
    },
  },
  {
    name: LAUNCH_ROUTES_PATHS_V2.CONTACT_ORGANISER_V2,
    path: LAUNCH_ROUTES_PATHS_V2.CONTACT_ORGANISER_V2 + "/:id",
    component: () =>
      import("../../competition/askorganiser/AskOrganiserRoute.vue"),
    meta: {
      title: (params: { id: string }) => {
        return params.id + ": Contact";
      },
    },
  },
  {
    name: LAUNCH_ROUTES_PATHS_V2.CLUB_SEARCH_V2,
    path: LAUNCH_ROUTES_PATHS_V2.CLUB_SEARCH_V2,
    beforeEnter: isLoggedInGuardV2,
    component: () => {
      return import("../../club/v2/club-search.vue");
    },
    meta: {
      title: () => {
        return "Clubs";
      },
    },
  },
  {
    name: LAUNCH_ROUTES_PATHS_V2.CLUB_V2,
    path: LAUNCH_ROUTES_PATHS_V2.CLUB_V2 + "/:id",
    component: () => {
      return import("../../club/v2/club-route.vue");
    },
    meta: {
      title: (params: { id: string }) => {
        return params.id + ": Club";
      },
    },
  },
  {
    name: LAUNCH_ROUTES_PATHS_V2.ORG_SEARCH_V2,
    path: LAUNCH_ROUTES_PATHS_V2.ORG_SEARCH_V2,
    beforeEnter: isLoggedInGuardV2,
    component: () => {
      return import("../../org/ui/OrgsRoute.vue");
    },
    meta: {
      title: () => {
        return "Organisations";
      },
    },
  },
  {
    name: LAUNCH_ROUTES_PATHS_V2.SHOP_V2,
    path: LAUNCH_ROUTES_PATHS_V2.SHOP_V2 + "/:id",
    component: () => {
      return import("../../secondary/v2/secondary-public-v2.vue");
    },
    meta: {
      title: (params: { id: string }) => {
        return params.id + ": Shop";
      },
    },
  },
  {
    name: "shop-v3",
    path: "shop-v3/:id",
    component: () => {
      return import("../../secondary/v3/shop-route-v3.vue");
    },
    meta: {
      title: (params: { id: string }) => {
        return params.id + ": Shop V3";
      },
    },
  },
  {
    name: LAUNCH_ROUTES_PATHS_V2.CHECK_IN_V2,
    path: LAUNCH_ROUTES_PATHS_V2.CHECK_IN_V2 + "/:id",
    component: () => {
      return import(
        "../../competition/checkin/checkin-organiser/checkin-organiser.vue"
      );
    },
    meta: {
      title: (params: { id: string }) => {
        return params.id + ": Check In";
      },
    },
  },
  {
    name: LAUNCH_ROUTES_PATHS_V2.TICKET_ADMIN_V2,
    path: LAUNCH_ROUTES_PATHS_V2.TICKET_ADMIN_V2 + "/:compId",
    component: () => {
      return import(
        "../../secondary/ticket/ticket-admin/ticket-admin-route.vue"
      );
    },
    meta: {
      title: (params: { compId: string }) => {
        return params.compId + ": Ticket Admin";
      },
    },
  },
  {
    name: LAUNCH_ROUTES_PATHS_V2.TICKET_FORM_GATEKEEPER,
    path: LAUNCH_ROUTES_PATHS_V2.TICKET_FORM_GATEKEEPER + "/:compId",
    component: () => {
      return import(
        "../../secondary/ticket/ticket-gatekeeper/ticket-route-gatekeeper.vue"
      );
    },
    meta: {
      title: (params: { compId: string }) => {
        return params.compId + ": Ticket Admin";
      },
    },
  },
  {
    name: LAUNCH_ROUTES_PATHS_V2.TICKET_FORM_V2,
    path: LAUNCH_ROUTES_PATHS_V2.TICKET_FORM_V2 + "/:compId/:id",
    component: () => {
      return import("../../secondary/ticket/ticket-route.vue");
    },
    meta: {
      title: "Ticket",
    },
  },
  {
    name: LAUNCH_ROUTES_PATHS_V2.RESULTS_MANUAL,
    path: LAUNCH_ROUTES_PATHS_V2.RESULTS_MANUAL + "/:compId",
    beforeEnter: isLoggedInGuardV2,
    component: () => {
      return import(
        "../../competition/results/public/schedule/results-public-route.vue"
      );
    },
    meta: {
      title: "Results",
    },
  },
  {
    name: LAUNCH_ROUTES_PATHS_V2.RESULTS_ENTRY,
    path: LAUNCH_ROUTES_PATHS_V2.RESULTS_ENTRY + "/:compId/:eventGroupId",
    beforeEnter: isLoggedInGuardV2,
    component: () => {
      return import("../../competition/results/result-route.vue");
    },
    meta: {
      title: "Results Entry",
    },
  },
  {
    name: LAUNCH_ROUTES_PATHS_V2.RESULTS_IMPORT_FILE,
    path: LAUNCH_ROUTES_PATHS_V2.RESULTS_IMPORT_FILE + "/:id",
    beforeEnter: isLoggedInGuardV2,
    component: () => {
      return import(
        "../../competition/results/results-file-import/ResultsImportFileRoute.vue"
      );
    },
    meta: {
      title: () => {
        return "Photo Finish";
      },
    },
  },
  {
    name: LAUNCH_ROUTES_PATHS_V2.RESULTS_IMPORT_V2,
    path: LAUNCH_ROUTES_PATHS_V2.RESULTS_IMPORT_V2 + "/:compId",
    beforeEnter: isLoggedInGuardV2,
    component: () => {
      return import(
        "../../competition/results/results-file-import/ResultsImportFileRoute.vue"
      );
    },
    meta: {
      title: (params: { compId: string }) => {
        return params.compId + ": Results Import";
      },
    },
  },
  {
    name: LAUNCH_ROUTES_PATHS_V2.SCOREBOARD_OUTPUT_LIST,
    path: LAUNCH_ROUTES_PATHS_V2.SCOREBOARD_OUTPUT_LIST + "/:compId",
    component: () => {
      // ../competition/scoreboard/scoreboard-schedule/scoreboard-schedule-list/scoreboard-schedule-list-route.vue
      return import(
        "../../competition/scoreboard/scoreboard-output/list/scoreboard-output-list-route.vue"
      );
    },
    meta: {
      title: (params: { compId: string; outputId: string }) => {
        return params.compId + "/" + params.outputId + ": Scoreboard Sched";
      },
    },
  },
  //  bibs
  {
    name: LAUNCH_ROUTES_PATHS_V2.BIBS_V2,
    path: LAUNCH_ROUTES_PATHS_V2.BIBS_V2 + "/:id",
    beforeEnter: isLoggedInGuardV2,
    component: () => {
      return import("../../competition/admin/bibs/bib-form-route.vue");
    },
    meta: {
      title: (params: { id: string }) => {
        return params.id + ": Bibs";
      },
    },
  },
  {
    name: LAUNCH_ROUTES_PATHS_V2.AGE_GROUP_LIST_V2,
    path: LAUNCH_ROUTES_PATHS_V2.AGE_GROUP_LIST_V2 + "/:compId",
    beforeEnter: isLoggedInGuardV2,
    component: () => {
      return import("../../agegroup/V2/presentation/age-group-list-v2.vue");
    },
    meta: {
      title: "Age Groups",
    },
  },
  {
    name: LAUNCH_ROUTES_PATHS_V2.AGE_GROUP_EDIT_V2,
    path: LAUNCH_ROUTES_PATHS_V2.AGE_GROUP_EDIT_V2 + "/:id",
    beforeEnter: isLoggedInGuardV2,
    component: () => {
      return import("../../agegroup/V2/presentation/age-group-edit-v2.vue");
    },
    meta: {
      title: "Edit Age Group",
    },
  },
  {
    name: LAUNCH_ROUTES_PATHS_V2.XC_UPLOAD_V3,
    path: LAUNCH_ROUTES_PATHS_V2.XC_UPLOAD_V3 + "/:compId",
    beforeEnter: isLoggedInGuardV2,
    component: () => {
      return import("../../competition/results/xc-upload/xc-upload-route.vue");
    },
    meta: {
      title: "Cross Country Upload",
    },
  },
  {
    name: LAUNCH_ROUTES_PATHS_V2.COMP_EVENT_ACTIONS_V2,
    path:
      LAUNCH_ROUTES_PATHS_V2.COMP_EVENT_ACTIONS_V2 +
      "/:compId/:athleteId/:clubId/:entryId",
    beforeEnter: isLoggedInGuardV2,
    component: () => {
      return import(
        "../../athleteCompSched/comp-event-actions/v2/comp-event-actions-v2.vue"
      );
    },
    meta: {
      title: "Comp Actions",
    },
  },
  {
    name: LAUNCH_ROUTES_PATHS_V2.STRIPE_USERS_V2,
    path: LAUNCH_ROUTES_PATHS_V2.STRIPE_USERS_V2,
    beforeEnter: isLoggedInGuardV2,
    component: () => {
      // return import("../../admin/stripe/StripeUsersWrapper.vue"); //"../../admin/stripe/StripeUsersWrapper.vue"
      return import("../../admin/stripe/StripeUsersWrapper.vue");
    },
    meta: {
      title: "Stripe Users",
    },
  },
  {
    name: LAUNCH_ROUTES_PATHS_V2.HELP_FORM_LIST_V2,
    path: LAUNCH_ROUTES_PATHS_V2.HELP_FORM_LIST_V2,
    beforeEnter: isLoggedInGuardV2,
    component: () => {
      return import("../../common/ui/field/field-help/help-form-list.vue");
    },
    meta: {
      title: "Help Config",
    },
  },
  {
    name: LAUNCH_ROUTES_PATHS_V2.SECONDARY_GRID_V2,
    path: LAUNCH_ROUTES_PATHS_V2.SECONDARY_GRID_V2,
    beforeEnter: isLoggedInGuardV2,
    component: () => {
      return import("../../secondary/config/secondary-form-grid.vue");
    },
    meta: {
      title: "Store Config",
    },
  },
  {
    name: LAUNCH_ROUTES_PATHS_V2.REPORTS_V2,
    path: LAUNCH_ROUTES_PATHS_V2.REPORTS_V2,
    beforeEnter: isLoggedInGuardV2,
    component: () => {
      return import("../../admin/reports/reports-container.vue");
    },
    meta: {
      title: "Reports",
    },
  },
  {
    name: LAUNCH_ROUTES_PATHS_V2.WAITLIST_V2,
    path: LAUNCH_ROUTES_PATHS_V2.WAITLIST_V2 + "/:compId",
    beforeEnter: isLoggedInGuardV2,
    component: () => {
      return import("../../waitlist/WaitlistRoute.vue");
    },
    meta: {
      title: (params: { compId: string }) => {
        return params.compId + ": Waitlist";
      },
    },
  },
  {
    name: "mobile-test-v2",
    path: "mobile-test-v2",
    component: () => {
      return import("../../test/mobile-test-v2.vue");
    },
    meta: {
      title: "Mobile Test V2",
    },
  },
];

export const widerRoutesClass: Record<LaunchRouteValueV2, string> = {
  "home-v2": "e4s-width-controller-entry",
  "builder-choice-v2": "e4s-width-controller--builder",
  "builder-v2": "e4s-width-controller--builder",
  "stepper-builder-v2": "e4s-width-controller--builder",
};

// "../competition/results/results-file-import/ResultsImportFileRoute.vue"
// "../competition/results/results-file-import/ResultsImportFileRoute.vue"
